import {
    CONDITION_TOLERANCE,
    DEFAULT_WINDOW_WEEKS,
    LOW_CONFIDENCE_DISCOUNT,
    MIN_BASELINE_FINDINGS_FOR_STANDING,
    MIN_BASELINE_WEEKS_FOR_STANDING,
    MIN_CLIENTS_FOR_A_RATE,
    MIN_FINDINGS_FOR_TREND,
    MIN_RECENT_FINDINGS_TO_FLAG,
    MIN_WEEKS_WITH_FINDINGS,
    RECENT_ROLLING_WEEKS,
    RECENT_WEEKS,
    SEVERITY_BY_KIND
} from './agencyThresholds';
import { INCIDENT_TERMS } from './agencyFindingKind';

/**
 * What each outcome figure counts, written once.
 *
 * This is the single source of truth for the definitions. The drill-down renders them in full, the
 * Outcomes Report tip renders each summary, and agencyOutcomes.ts computes what is written here
 * rather than restating it. A change to how a figure is computed changes its entry here in the same
 * commit, and the seeded caseload in agencyOutcomesCaseload.tests.ts records what the change did.
 *
 * Every number comes from agencyThresholds.ts, and the incident words from agencyFindingKind.ts, so
 * neither can move without its definition moving with it.
 */

export type OutcomeMetricId = 'back-from-a-decline' | 'settled-below-usual' | 'no-incident-recorded';

/** One figure's definition, in the parts a reader checks before repeating it to anyone else. */
export interface IOutcomeDefinition {
    title: string;
    /** One sentence: what the figure counts. */
    summary: string;
    /** What it reads, and which clients it considers at all. */
    inputs: string;
    window: string;
    thresholds: string;
    leavesOut: string;
    /** What the figure does establish. */
    shows: string;
    /**
     * What it does not, stated as plainly as what it does. The one place the claims these figures
     * must never make are named, so it is the one field the causal-word test lets them appear in.
     */
    doesNotShow: string;
    /** What the card shows when there is too little to go on. */
    whenThin: string;
    /**
     * Compares each client with their own earlier weeks, so the report holds it back until the
     * caseload has the history for that. A figure that reads only the last 7 days compares nothing.
     */
    needsHistory: boolean;
}

type DefinitionPart = Exclude<keyof IOutcomeDefinition, 'title' | 'summary' | 'needsHistory'>;

/** The order the parts are read in, and the heading each sits under. */
export const DEFINITION_PARTS: { key: DefinitionPart; heading: string }[] = [
    { key: 'inputs', heading: 'What it reads' },
    { key: 'window', heading: 'Over what period' },
    { key: 'thresholds', heading: 'Where the lines are' },
    { key: 'leavesOut', heading: 'What it leaves out' },
    { key: 'shows', heading: 'What it shows' },
    { key: 'doesNotShow', heading: 'What it does not show' },
    { key: 'whenThin', heading: 'When there is too little to go on' }
];

const READING =
    'The findings written about each client, read the way the caseload map reads them: for each part '
    + 'of daily life, the most serious thing raised about it, added up across the six. Something that '
    + `happened counts ${SEVERITY_BY_KIND.incident} points, something that stood out ${SEVERITY_BY_KIND.baseline}, `
    + `anything else ${SEVERITY_BY_KIND.observation}, and ${LOW_CONFIDENCE_DISCOUNT} less when the model was `
    + 'not confident. A part of life counts once however many findings raise it, so a household writing '
    + 'more about the same things reads the same. Findings about the arrangements around a client, such '
    + 'as a booking or a refill, count for nothing.';

const HAS_A_USUAL =
    `A client has a usual only once at least ${MIN_BASELINE_FINDINGS_FOR_STANDING} findings about them `
    + `fall in at least ${MIN_BASELINE_WEEKS_FOR_STANDING} different weeks before the recent part of the window, `
    + 'counting only the part of those weeks the map reads: it leaves out the oldest days of the window, '
    + `up to ${(DEFAULT_WINDOW_WEEKS - RECENT_WEEKS) % RECENT_ROLLING_WEEKS} weeks of them on a Monday.`;

const CASELOAD_HISTORY =
    `While the caseload as a whole has fewer than ${MIN_FINDINGS_FOR_TREND} findings in the chart's `
    + `${DEFAULT_WINDOW_WEEKS} week bars, or they fall in fewer than ${MIN_WEEKS_WITH_FINDINGS} of them, the report `
    + 'shows what it is still waiting for in place of this figure.';

const SHOWS_AT_ANY_AGE = 'It shows however new the caseload is, because it compares nothing with earlier weeks.';

/** Writes the history sentence from needsHistory, the flag the report gates on, so the copy cannot contradict the gate. */
const withHistoryRule = (definition: IOutcomeDefinition): IOutcomeDefinition => ({
    ...definition,
    whenThin: `${definition.whenThin} ${definition.needsHistory ? CASELOAD_HISTORY : SHOWS_AT_ANY_AGE}`
});

const WITH_ITS_BASE =
    'The count is always shown with the number of clients who have a usual to compare against. With '
    + 'none, no figure is shown, rather than a zero that would read as a finding about people nobody '
    + 'could assess.';

const INCIDENT =
    'An incident is a finding that stood out with a word in its title or narrative that starts with one '
    + `of ${INCIDENT_TERMS.map(term => `"${term}"`).join(', ')}. The api does not mark incidents, so this `
    + 'reads the words the model wrote, and a passing mention counts: "falling asleep" or "a hospital '
    + 'appointment" reads as an incident too.';

const NOT_THE_CAUSE =
    'Whether anything Innerhive or the agency did caused it: nobody has measured what would have '
    + 'happened otherwise.';

export const OUTCOME_DEFINITIONS: { [id in OutcomeMetricId]: IOutcomeDefinition } = {
    'back-from-a-decline': withHistoryRule({
        title: 'Back From a Decline',
        summary:
            `Clients who were above their own usual in one of the ${RECENT_ROLLING_WEEKS - 1} weeks before the `
            + 'last 7 days, and are back within it now.',
        inputs: `${READING} ${INCIDENT} Also when each care map was last written to.`,
        window:
            `The last ${RECENT_ROLLING_WEEKS} weeks, as rolling 7-day spans ending when the tab was read: as `
            + 'many whole weeks as always fit in the recent part of the window. Each is judged against the '
            + `client's usual, which is their average week across the chart's ${DEFAULT_WINDOW_WEEKS - RECENT_WEEKS} `
            + `week bars before the recent part. Days of the recent part before the last ${RECENT_ROLLING_WEEKS} `
            + 'weeks are not read.',
        thresholds:
            'Above their usual in a week: an incident recorded that week, or at least '
            + `${MIN_RECENT_FINDINGS_TO_FLAG} findings about them with the week reading more than `
            + `${CONDITION_TOLERANCE} point above their usual. Back within it: the last 7 days read no more `
            + `than ${CONDITION_TOLERANCE} point above their usual, with no incident and something recorded. `
            + HAS_A_USUAL,
        leavesOut:
            'Clients without a usual yet, however they are doing. A household that wrote nothing in the '
            + 'last 7 days: a quiet record is not a client who is back. Anyone with an incident in the last '
            + '7 days. Clients whose record this organization cannot read.',
        shows:
            'That what was written about a client moved above their own usual and has come back within '
            + 'it, and what was written down in between.',
        doesNotShow:
            `${NOT_THE_CAUSE} Nor that an escalation, a visit or an admission was avoided. Findings carry `
            + 'no direction, so how serious one is comes from its kind, and a household writing much less, '
            + 'but not nothing, can read as back. A client can be on this list and still be drawn as '
            + 'Declining on the map, which reads the recent weeks together where this reads the last 7 days. '
            + 'And a client can be on this list and on Settled Below Their Usual at once: one week above their '
            + 'average week can sit inside recent weeks that read lower overall than their earlier ones.',
        whenThin: WITH_ITS_BASE,
        needsHistory: true
    }),

    'settled-below-usual': withHistoryRule({
        title: 'Settled Below Their Usual',
        summary:
            'Clients with more than a little less raised about them over the recent weeks than in their own '
            + 'earlier weeks, no incident recorded, and the household still writing: the clients the map draws '
            + 'as Settled.',
        inputs: `${READING} ${INCIDENT} Also when each care map was last written to.`,
        window:
            `The recent weeks, which are the last ${RECENT_WEEKS} week bars of the chart and so `
            + `${RECENT_WEEKS - 1} to ${RECENT_WEEKS} weeks depending on the day, against spans of the same length in the `
            + `${DEFAULT_WINDOW_WEEKS - RECENT_WEEKS} weeks before them.`,
        thresholds:
            `The recent reading more than ${CONDITION_TOLERANCE} point below their usual, and something recorded `
            + `during the recent weeks. ${HAS_A_USUAL}`,
        leavesOut:
            'Clients without a usual yet. Anyone with an incident in the recent weeks, whatever else the '
            + 'record says. A household with nothing recorded in the recent weeks. Clients whose record this '
            + 'organization cannot read.',
        shows: 'That less has been raised about a client recently than their own record used to show.',
        doesNotShow:
            'That the client is better, or why. A household writing much less, but not nothing, covers less '
            + 'ground and reads the same as a client with less going on, and the record cannot tell the two '
            + `apart. ${NOT_THE_CAUSE} And a client can be on this list and on Back From a Decline at once: one `
            + 'week above their average week can sit inside recent weeks that read lower overall than their '
            + 'earlier ones.',
        whenThin: WITH_ITS_BASE,
        needsHistory: true
    }),

    'no-incident-recorded': withHistoryRule({
        title: 'No Incident Recorded',
        summary: 'Of the clients with something recorded in the last 7 days, how many had no incident on the record in that time.',
        inputs: `Findings from the last 7 days, and when each care map was last written to. ${INCIDENT}`,
        window: 'The last 7 days, rolling, ending when the tab was read.',
        thresholds:
            `One incident is enough. Shown as a percentage once at least ${MIN_CLIENTS_FOR_A_RATE} clients `
            + 'are counted, rounded down so any incident keeps it under 100%, and as two counts below that.',
        leavesOut:
            'Clients with nothing recorded in the last 7 days, who are listed but not counted either way: a '
            + 'quiet record is not a safe week. Clients whose record this organization cannot read. Any '
            + 'change on the week before, because the record cannot say who was writing then.',
        shows: 'How many of the clients whose households are writing had an incident written up in the last 7 days.',
        doesNotShow:
            'That anyone was safe, stayed home or kept out of hospital. The word match misses an incident '
            + 'nobody wrote down or one described in other words, and counts a passing mention as one. A '
            + 'finding exists only once the record has been read for patterns, so the newest incident can be '
            + `missing. It is not a claims or admissions feed. ${NOT_THE_CAUSE}`,
        whenThin:
            `A percentage only from ${MIN_CLIENTS_FOR_A_RATE} clients counted. Fewer, and the card shows the `
            + 'two counts. With nobody counted, it shows no figure.',
        needsHistory: false
    })
};
